import React from 'react';
import { Alert, Input, InputNumber, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { InvoiceReceiptCost } from '../api/invoiceWorkflowApi';
import { formatCurrency } from '../../../utils/currency';
import { formatUnit } from '../../../utils/units';
import './InvoiceReceiptCostReview.css';

export interface ReceiptCostEdit { purchaseInvoiceItemId: string; totalCostPLN: number | null; reason?: string }
interface Props {
  rows: InvoiceReceiptCost[];
  values: ReceiptCostEdit[];
  onChange: (values: ReceiptCostEdit[]) => void;
  disabled: boolean;
  issue?: { message: string };
}
export default function InvoiceReceiptCostReview({ rows, values, onChange, disabled, issue }: Props) {
  const { t } = useTranslation('invoices');
  const update = (row: InvoiceReceiptCost, patch: Partial<ReceiptCostEdit>) => {
    const current = values.find(value => value.purchaseInvoiceItemId === row.purchaseInvoiceItemId);
    onChange([...values.filter(value => value.purchaseInvoiceItemId !== row.purchaseInvoiceItemId),
      { purchaseInvoiceItemId: row.purchaseInvoiceItemId, totalCostPLN: row.proposedTotalCostPLN, ...current, ...patch }]);
  };
  return <section className="invoice-receipt-cost" aria-label={t('workflow.receiptCost.title')}>
    <div className="invoice-receipt-cost__heading">
      <Typography.Text strong>{t('workflow.receiptCost.title')}</Typography.Text>
      <Typography.Text type="secondary">{t('workflow.receiptCost.hint')}</Typography.Text>
    </div>
    {issue && <Alert type="warning" showIcon message={issue.message} />}
    {rows.map(row => {
      const edited = values.find(value => value.purchaseInvoiceItemId === row.purchaseInvoiceItemId);
      const amount = edited ? edited.totalCostPLN : row.proposedTotalCostPLN;
      const changed = amount != null && amount !== row.proposedTotalCostPLN;
      return <div className="invoice-receipt-cost__line" key={row.purchaseInvoiceItemId}>
        <div className="invoice-receipt-cost__product">
          <Typography.Text strong>{row.productName}</Typography.Text>
          <Typography.Text type="secondary">{row.quantity.toLocaleString('pl-PL')} {formatUnit(row.unit)}</Typography.Text>
          <Typography.Text type="secondary">{t('workflow.receiptCost.source', { invoice: formatCurrency(row.invoiceNetPLN, 'PLN'), extras: formatCurrency(row.includedExtraCostPLN, 'PLN') })}</Typography.Text>
        </div>
        <div className="invoice-receipt-cost__amount">
          <label htmlFor={`receipt-cost-${row.purchaseInvoiceItemId}`}>{t('workflow.receiptCost.amount')}</label>
          <InputNumber id={`receipt-cost-${row.purchaseInvoiceItemId}`} aria-label={`${t('workflow.receiptCost.amount')}: ${row.productName}`} value={amount} min={0} max={1000000000} precision={2} decimalSeparator="," addonAfter="PLN" disabled={disabled} onChange={totalCostPLN => update(row, { totalCostPLN })} status={amount == null ? 'error' : undefined} />
          <div className="invoice-receipt-cost__unit-costs">
            {([['pcs', row.quantityPcs], ['m2', row.quantityM2], ['m3', row.quantityM3]] as const).map(([unit, quantity]) =>
              <Typography.Text type="secondary" key={unit}>
                {amount != null && quantity != null && quantity > 0 ? formatCurrency(amount / quantity, 'PLN') : '—'} / {formatUnit(unit)}
              </Typography.Text>)}
          </div>
          {changed && <Input aria-label={`${t('workflow.receiptCost.reason')}: ${row.productName}`} placeholder={t('workflow.receiptCost.reason')} value={edited?.reason ?? ''} maxLength={500} disabled={disabled} onChange={event => update(row, { reason: event.target.value })} />}
        </div>
      </div>;
    })}
  </section>;
}
